import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Heart, Trash2, Send, Loader2, ImageOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const WORKER_BASE = (
  process.env.REACT_APP_PAPI_WORKER_URL
  || "https://preguntale-a-papi.guendelman.workers.dev"
).replace(/\/ask-papi\/?$/, "").replace(/\/+$/, "");

function timeAgo(iso) {
  const d = new Date(iso);
  const now = new Date();
  const s = Math.round((now - d) / 1000);
  if (s < 60) return "hace un momento";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  return d.toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" });
}

export default function Gallery() {
  const { member, displayName, logout } = useAuth();
  const nav = useNavigate();
  const [plates, setPlates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState({}); // { plateId: text }
  const [sending, setSending] = useState({}); // { plateId: bool }
  const [deleting, setDeleting] = useState({}); // { plateId: bool }

  const canPost = member && member !== "invitado";

  useEffect(() => {
    // Los invitados no ven la galería (privado de familia)
    if (member === "invitado") {
      toast.error("La galería es solo para la familia");
      nav("/cocina");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_BASE}/list-plates`);
      const json = await res.json();
      if (json.ok) setPlates(json.plates || []);
    } catch {
      toast.error("No pudimos cargar la galería");
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (plate) => {
    const text = (commentText[plate.id] || "").trim();
    if (!text) return;
    setSending((s) => ({ ...s, [plate.id]: true }));
    try {
      const res = await fetch(`${WORKER_BASE}/comment-plate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: plate.id, member, displayName, text,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "comment_failed");
      setPlates((ps) => ps.map((p) => p.id === plate.id ? json.plate : p));
      setCommentText((c) => ({ ...c, [plate.id]: "" }));
    } catch (e) {
      toast.error(`No se pudo comentar: ${e.message || e}`);
    } finally {
      setSending((s) => ({ ...s, [plate.id]: false }));
    }
  };

  const handleDelete = async (plate) => {
    // Solo dueño o papa
    if (plate.member !== member && member !== "papa") return;
    if (!window.confirm(`¿Borrar la foto de ${plate.recipe_name || "esta receta"}?`)) return;
    setDeleting((d) => ({ ...d, [plate.id]: true }));
    try {
      const res = await fetch(`${WORKER_BASE}/delete-plate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: plate.id, member }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || "delete_failed");
      setPlates((ps) => ps.filter((p) => p.id !== plate.id));
      toast.success("Foto eliminada");
    } catch (e) {
      toast.error(`No se pudo borrar: ${e.message || e}`);
    } finally {
      setDeleting((d) => ({ ...d, [plate.id]: false }));
    }
  };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link data-testid="gallery-back-btn" to="/cocina" className="p-2 rounded-full bg-cream border border-line">
            <ChevronLeft className="w-5 h-5 text-cocoa" />
          </Link>
          <div className="text-center">
            <p className="eyebrow text-[10px]">Familia Guendelman</p>
            <h1 className="font-display italic text-cocoa text-2xl leading-tight">Los platos de la familia</h1>
          </div>
          <div className="w-9 h-9" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-20 pt-6">
        {loading && (
          <div className="text-center py-20 text-cocoa-soft font-serif italic">Cargando galería…</div>
        )}
        {!loading && plates.length === 0 && (
          <div className="text-center py-20">
            <ImageOff className="w-14 h-14 text-amber-deep/40 mx-auto mb-4" />
            <p className="font-display italic text-cocoa text-2xl mb-2">Aún no hay platos</p>
            <p className="font-serif text-cocoa-soft italic">
              Cuando termines una receta en Modo Cocina, sube la foto para que la familia la vea.
            </p>
          </div>
        )}

        <ul data-testid="gallery-plates-list" className="space-y-6">
          {plates.map((plate) => {
            const canDelete = plate.member === member || member === "papa";
            return (
              <li
                key={plate.id}
                data-testid={`plate-${plate.id}`}
                className="bg-cream border border-line rounded-2xl overflow-hidden shadow-[0_20px_40px_-22px_rgba(58,26,5,0.25)]"
              >
                <div className="relative w-full aspect-square bg-line">
                  <img
                    src={plate.url}
                    alt={plate.recipe_name || "Plato familiar"}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  {canDelete && (
                    <button
                      data-testid={`plate-delete-${plate.id}`}
                      onClick={() => handleDelete(plate)}
                      disabled={!!deleting[plate.id]}
                      className="absolute top-3 right-3 p-2 rounded-full bg-paper/90 backdrop-blur-md border border-line text-cocoa hover:text-red-600 transition"
                      aria-label="Borrar foto"
                    >
                      {deleting[plate.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-baseline justify-between mb-2">
                    <p className="font-display italic text-cocoa text-xl leading-tight">
                      {plate.recipe_name || "Un plato rico"}
                    </p>
                    <p className="text-xs italic text-cocoa-soft whitespace-nowrap ml-2">
                      {timeAgo(plate.created_at)}
                    </p>
                  </div>
                  <p className="text-sm font-serif text-cocoa-soft mb-4">
                    Cocinado por <b className="text-amber-deep">{plate.display_name || plate.member}</b>
                    {plate.recipe_slug && (
                      <>
                        {" · "}
                        <Link
                          to={`/receta/${plate.recipe_slug}`}
                          className="text-amber-deep italic underline hover:no-underline"
                        >
                          ver la receta
                        </Link>
                      </>
                    )}
                  </p>

                  {/* Comentarios */}
                  {plate.comments && plate.comments.length > 0 && (
                    <ul className="space-y-2 mb-4 border-t border-line pt-3">
                      {plate.comments.map((c, i) => (
                        <li key={i} className="flex gap-2 items-start">
                          <Heart className="w-3.5 h-3.5 text-amber-deep mt-1 shrink-0" />
                          <div>
                            <p className="text-sm font-serif text-cocoa">
                              <b className="text-amber-deep">{c.author}</b>: {c.text}
                            </p>
                            <p className="text-xs italic text-cocoa-soft">{timeAgo(c.at)}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {canPost && (
                    <form
                      onSubmit={(e) => { e.preventDefault(); handleComment(plate); }}
                      className="flex gap-2 items-center"
                    >
                      <input
                        data-testid={`plate-comment-input-${plate.id}`}
                        type="text"
                        value={commentText[plate.id] || ""}
                        onChange={(e) => setCommentText((c) => ({ ...c, [plate.id]: e.target.value }))}
                        placeholder="Escribe algo lindo…"
                        maxLength={400}
                        className="flex-1 px-3 py-2 rounded-full bg-paper border border-line text-cocoa font-serif text-sm focus:outline-none focus:ring-2 focus:ring-amber-deep/40"
                      />
                      <button
                        data-testid={`plate-comment-submit-${plate.id}`}
                        type="submit"
                        disabled={!!sending[plate.id] || !(commentText[plate.id] || "").trim()}
                        className="w-9 h-9 rounded-full bg-amber-deep text-cream flex items-center justify-center disabled:opacity-30"
                        aria-label="Enviar comentario"
                      >
                        {sending[plate.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </form>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}
